import { z } from "zod";

// Base Types
export type ContentGroupType = 'CATEGORY' | 'PLAN_GROUP';
export type FilterType = 'all' | 'channel' | 'plan' | 'category';
export type ChangeType = 'CREATE' | 'UPDATE' | 'DELETE' | 'all';
export type GroupBy = 'none' | 'type' | 'date';

// Schemas
export const contentGroupSchema = z.object({
  id: z.number().optional(),
  name: z.string().min(1, "Name is required"),
  type: z.enum(["CATEGORY", "PLAN_GROUP"]),
  categoryId: z.number().optional().nullable(),
  planId: z.number().optional().nullable(),
  versionId: z.number().optional(),
  displayOrder: z.number().optional()
});

export const versionSchema = z.object({
  provider_id: z.number(),
  version_number: z.string().optional(),
  description: z.string().optional()
});

export const publishVersionSchema = z.object({
  effective_date: z.string()
    .min(1, 'Effective date is required for publishing'),
  description: z.string().optional()
});

// Form Types
export type ContentGroupFormData = z.infer<typeof contentGroupSchema>;
export type CreateVersionInput = z.infer<typeof versionSchema> & {
  copyFromVersion?: number;
};
export type PublishVersionInput = z.infer<typeof publishVersionSchema>;

// Entity Types
export interface Version {
  id: number;
  provider_id: number;
  region_id?: number;
  version_number: string;
  status: 'draft' | 'published';
  description?: string;
  effective_date?: string;
  created_at: string;
  published_at?: string;
}

export interface ContentGroup {
  id: number;
  name: string;
  type: "CATEGORY" | "PLAN_GROUP";
  categoryId?: number | null;
  planId?: number | null;
  versionId: number;
  displayOrder: number;
  plan?: {
    id: number;
    name: string;
    color_index: number;
  };
}

// Channel Types
export interface Channel {
  id: number;
  name: string;
  number: number;
  version_id: number;
  price?: number;
  description?: string;
}

export interface ChannelWithRelations extends Channel {
  category?: VersionCategory;
  plans?: VersionPlan[];
  numbers?: {
    id: number;
    type_id: number;
    number: string;
  }[];
}

export interface ChannelChange {
  field: string;
  previous: any;
  current: any;
}

// Version Change Types
export interface VersionChange {
  id: number;
  version_id: number;
  entity_type: string;
  entity_id: number;
  change_type: 'CREATE' | 'UPDATE' | 'DELETE';
  changes: {
    previous?: any;
    current?: any;
    [key: string]: any;
  };
  created_at: string;
}

export interface VersionCategory {
  id: number;
  name: string;
}

export interface VersionPlan {
  id: number;
  name: string;
  color: string;
}

// Comparison Types
export interface ComparisonResult {
  added: ChannelWithRelations[];
  removed: ChannelWithRelations[];
  modified: {
    channel: ChannelWithRelations;
    changes: ChannelChange[];
  }[];
}

// Props Types
export interface VersionAwareProps {
  providerId: number;
  versionId: number;
  isReadOnly?: boolean;
}

export interface ComparisonTableProps {
  data?: ComparisonResult;
  isLoading: boolean;
  baseVersion?: Version;
  compareVersion?: Version;
}

export interface ChangeHistoryViewProps {
  providerId: number;
  versionId: number;
  onChangeSelect?: (change: VersionChange) => void;
}

export interface VersionChangesModalProps {
  change: VersionChange | null;
  isOpen: boolean;
  onClose: () => void;
} 

export interface ChangeFilters {
  entityType: FilterType;
  changeType: ChangeType;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export type VersionWithChanges = Version & { changes: VersionChange[] };
export type UniversalHeaderProps = {
  providerId: number;
  versionId: number;
}